// Hook partagé : acceptation in-app d'une invitation en attente, pour un user
// déjà connecté (cas multi-org). Appelle l'action serveur
// `invitations.acceptMyPending` qui crée le membership WorkOS directement
// (court-circuite le lien email cassé pour un user existant), puis confirme
// via le dialog. Consommé par PendingInvitationsBanner ET NotificationsTray
// pour partager l'état "en cours" + la gestion d'erreur.

import { useAction } from "convex/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useDialog } from "@/shell/dialog";

export function useAcceptInvitation() {
  const { t } = useTranslation();
  const dialog = useDialog();
  const accept = useAction(api.invitations.acceptMyPending);
  const [acceptingId, setAcceptingId] =
    useState<Id<"pendingInvitationPermissions"> | null>(null);

  async function acceptInvitation(
    pendingId: Id<"pendingInvitationPermissions">,
    accountName: string | null,
  ): Promise<void> {
    // Un seul accept à la fois (le bouton se disable globalement) — évite les
    // double-clics et les races sur deux invitations en parallèle.
    if (acceptingId) return;
    setAcceptingId(pendingId);
    try {
      const res = await accept({ pendingId });
      await dialog.alert({
        variant: "info",
        title: t("invitations.banner.joinedTitle"),
        message: t("invitations.banner.joinedMessage", {
          account: res.accountName ?? accountName ?? "—",
        }),
      });
    } catch (err) {
      // Les codes serveur (EMAIL_MISMATCH, etc.) ne sont pas user-friendly —
      // on log pour le debug et on montre un message générique actionnable.
      console.error("acceptMyPending failed", err);
      await dialog.alert({
        variant: "error",
        title: t("invitations.banner.errorTitle"),
        message: t("invitations.banner.errorFallback"),
      });
    } finally {
      setAcceptingId(null);
    }
  }

  return { acceptInvitation, acceptingId };
}
